import * as React from "react";
import MotwLogicServer from "source/app/services/External/LogicServer/MotwLogicServer";
import { StyleClass } from "source/app/presentation/Shared/Styles/CSS";

type UploadResult = {
    fileName: string;
    filePath: string;
    publicUrl: string;
    byteLength: number;
    mimeType: string;
};

export function FilePasteTool() {
    const [status, setStatus] = React.useState(
        "Paste or choose a file, then click Send."
    );
    const [draftText, setDraftText] = React.useState("");
    const [pendingFile, setPendingFile] = React.useState<File | null>(null);
    const [pendingPreviewUrl, setPendingPreviewUrl] = React.useState<
        string | null
    >(null);
    const [savedFileUrl, setSavedFileUrl] = React.useState<string | null>(
        null
    );
    const [savedFilePath, setSavedFilePath] = React.useState<string | null>(
        null
    );
    const [isFocused, setIsFocused] = React.useState(false);
    const fileInputRef = React.useRef<HTMLInputElement | null>(null);

    React.useEffect(() => {
        return () => {
            if (pendingPreviewUrl) {
                URL.revokeObjectURL(pendingPreviewUrl);
            }
        };
    }, [pendingPreviewUrl]);

    return (
        <div
            style={{
                marginTop: "10px",
                padding: "10px",
                border: `1px solid ${isFocused ? "#f0d08a" : "#666"}`,
                borderRadius: "8px",
                background: "rgba(0, 0, 0, 0.2)",
            }}
        >
            <div style={{ marginBottom: "8px", fontWeight: 600 }}>
                Paste File
            </div>
            <textarea
                value={draftText}
                onChange={(event) => setDraftText(event.target.value)}
                placeholder="Paste a file here"
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                onPaste={HandlePaste}
                style={{
                    width: "100%",
                    minHeight: "92px",
                    padding: "12px",
                    borderRadius: "6px",
                    border: "1px dashed #999",
                    outline: "none",
                    cursor: "text",
                    color: "#ddd",
                    background: isFocused ? "rgba(255, 255, 255, 0.06)" : "transparent",
                    resize: "vertical",
                    boxSizing: "border-box",
                }}
            />
            <div
                style={{
                    marginTop: "6px",
                    fontSize: "12px",
                    opacity: 0.85,
                    minHeight: "16px",
                }}
            >
                {status}
            </div>
            <div
                style={{
                    display: "flex",
                    gap: "8px",
                    marginTop: "8px",
                    flexWrap: "wrap",
                }}
            >
                <button
                    type="button"
                    onClick={HandleSendClick}
                    disabled={!pendingFile}
                    className={devToolsActionButtonClass}
                >
                    Send
                </button>
                <button
                    type="button"
                    onClick={HandleChooseFileClick}
                    className={devToolsActionButtonClass}
                >
                    Choose File
                </button>
                <button
                    type="button"
                    onClick={HandleClearClick}
                    className={devToolsActionButtonClass}
                >
                    Clear
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: "none" }}
                    onChange={HandleFileChange}
                />
            </div>
            {pendingPreviewUrl ? (
                <div style={{ marginTop: "10px" }}>
                    <img
                        src={pendingPreviewUrl}
                        alt="pending clipboard preview"
                        style={{
                            maxWidth: "100%",
                            borderRadius: "6px",
                            border: "1px solid rgba(255,255,255,0.2)",
                        }}
                    />
                </div>
            ) : null}
            {savedFileUrl ? (
                <div style={{ marginTop: "10px" }}>
                    <img
                        src={savedFileUrl}
                        alt="latest pasted upload"
                        style={{
                            maxWidth: "100%",
                            borderRadius: "6px",
                            border: "1px solid rgba(255,255,255,0.2)",
                        }}
                    />
                </div>
            ) : null}
            {savedFilePath ? (
                <div
                    style={{
                        marginTop: "8px",
                        fontSize: "12px",
                        opacity: 0.8,
                        wordBreak: "break-all",
                    }}
                >
                    Saved to: {savedFilePath}
                </div>
            ) : null}
        </div>
    );

    function HandleChooseFileClick() {
        fileInputRef.current?.click();
    }

    function HandleClearClick() {
        setDraftText("");
        ClearPendingFile();
        setSavedFileUrl(null);
        setSavedFilePath(null);
        setStatus("Paste or choose a file, then click Send.");
    }

    async function HandleFileChange(
        event: React.ChangeEvent<HTMLInputElement>
    ) {
        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        SetPendingFile(file);
        setStatus(`Loaded ${file.name}. Click Send to upload.`);
        event.target.value = "";
    }

    async function HandlePaste(
        event: React.ClipboardEvent<HTMLTextAreaElement>
    ): Promise<void> {
        const file = GetClipboardFile(event.clipboardData);

        if (!file) {
            setStatus("Clipboard does not contain a file.");
            return;
        }

        event.preventDefault();
        setDraftText("");
        SetPendingFile(file);
        setStatus(`Loaded ${file.name || "clipboard file"}. Click Send to upload.`);
    }

    async function HandleSendClick() {
        if (!pendingFile) {
            setStatus("No file loaded yet.");
            return;
        }

        setStatus(`Uploading ${pendingFile.name || "clipboard file"}...`);

        try {
            const dataUrl = await ReadFileAsDataUrl(pendingFile);
            const response = (await MotwLogicServer.UploadPastedFile(
                dataUrl,
                pendingFile.name
            )) as UploadResult;

            setSavedFileUrl(
                response.mimeType.startsWith("image/")
                    ? response.publicUrl
                    : null
            );
            setSavedFilePath(response.filePath);
            setStatus(
                `Saved ${response.fileName} (${FormatKilobytes(
                    response.byteLength
                )} KB)`
            );
        } catch (error) {
            setStatus(`Upload failed: ${GetErrorMessage(error)}`);
            console.error(error);
        }
    }

    function SetPendingFile(file: File) {
        ClearPendingFile();
        setPendingFile(file);
        setPendingPreviewUrl(
            file.type.startsWith("image/") ? URL.createObjectURL(file) : null
        );
    }

    function ClearPendingFile() {
        if (pendingPreviewUrl) {
            URL.revokeObjectURL(pendingPreviewUrl);
        }

        setPendingFile(null);
        setPendingPreviewUrl(null);
    }
}

function GetClipboardFile(clipboardData: DataTransfer) {
    const items = Array.from(clipboardData.items || []);
    const fileFromItem = items
        .filter((item) => item.kind === "file")
        .map((item) => item.getAsFile())
        .find((file): file is File => file !== null);

    if (fileFromItem) {
        return fileFromItem;
    }

    const fileFromFiles = Array.from(clipboardData.files || [])[0];

    return fileFromFiles || null;
}

function ReadFileAsDataUrl(file: File) {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error);
        reader.onload = () => {
            const result = reader.result;

            if (typeof result !== "string") {
                reject(new Error("Could not read file."));
                return;
            }

            resolve(result);
        };
        reader.readAsDataURL(file);
    });
}

const devToolsActionButtonClass = StyleClass(
    "devToolsPasteFileActionButton",
    {
        minWidth: "110px",
        minHeight: "36px",
        padding: "6px 12px",
        border: "1px solid #d9c26a",
        borderRadius: "4px",
        background: "rgba(217, 194, 106, 0.18)",
        color: "#f5e7a5",
        fontSize: "13px",
        fontWeight: 700,
        fontFamily: '"IM Fell French Canon SC", serif',
        letterSpacing: 0,
        textTransform: "uppercase",
        cursor: "pointer",
        boxShadow: "0 0 12px rgba(217, 194, 106, 0.18)",
        ":hover": {
            background: "rgba(217, 194, 106, 0.3)",
            color: "#fff3bc",
        },
        ":disabled": {
            cursor: "wait",
            opacity: 0.72,
            filter: "grayscale(1)",
        },
    }
);

function FormatKilobytes(byteLength: number) {
    return (byteLength / 1024).toFixed(1);
}

function GetErrorMessage(error: unknown) {
    if (error instanceof Error) {
        return error.message;
    }

    return "Unknown error";
}
